import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Icon, Input } from '@y365/design-system';
import {
  validateSubmission,
  type SectionDefinition,
  type ResolvedDefinition,
  type SubmissionPayload,
  type ValidationError,
} from '@y365/shared';
import { FieldRenderer } from './FieldRenderer.js';
import { ErrorSummary } from './ErrorSummary.js';

interface Props {
  definition: ResolvedDefinition;
  section: SectionDefinition;
  /** ISO date of a previous submission, or null if this is the first time. */
  previouslySubmittedOn?: string | null;
  initialValues: Record<string, unknown>;
  recipientName: string;
  recipientEmail: string;
  onSaveDraft: (values: Record<string, unknown>) => Promise<void>;
  onSubmit: (payload: SubmissionPayload) => Promise<ValidationError[] | null>;
  /**
   * Every keystroke, undebounced — unlike onSaveDraft.
   *
   * Switching sections unmounts this form, and remounting it reads
   * `initialValues` again. Without somewhere to put the current values, moving
   * between tabs would discard anything typed since the last autosave, which is
   * a plain data-loss bug rather than a rough edge.
   */
  onValuesChange?: (values: Record<string, unknown>) => void;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const AUTOSAVE_DEBOUNCE_MS = 1200;

export function SectionForm({
  definition,
  section,
  previouslySubmittedOn,
  initialValues,
  recipientName,
  recipientEmail,
  onSaveDraft,
  onSubmit,
  onValuesChange,
}: Props) {
  const [values, setValues] = useState<Record<string, unknown>>(initialValues);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [submitCount, setSubmitCount] = useState(0);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState(recipientName);
  const [email, setEmail] = useState(recipientEmail);

  const dirty = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const errorsByPath = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of errors) if (!map.has(e.path)) map.set(e.path, e.message);
    return map;
  }, [errors]);

  const setValue = useCallback(
    (path: string, value: unknown) => {
      setValues((prev) => {
        const next = { ...prev, [path]: value };
        onValuesChange?.(next);
        return next;
      });
      dirty.current = true;
    },
    [onValuesChange],
  );

  // Debounced autosave. Drafts go to Table Storage keyed by section id — never
  // to Dataverse, which stays clean for the immutable submission.
  useEffect(() => {
    if (!dirty.current) return;

    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setSaveState('saving');
      onSaveDraft(values)
        .then(() => setSaveState('saved'))
        .catch(() => setSaveState('error'));
      dirty.current = false;
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer.current);
  }, [values, onSaveDraft]);

  /*
    Flush an unsaved draft when this form goes away.

    Switching sections unmounts mid-debounce, so up to AUTOSAVE_DEBOUNCE_MS of
    typing would never reach Table Storage. The tab switch itself is covered by
    onValuesChange keeping the values in memory — this is for the harder case:
    the customer switches tabs, then closes the browser. Fire-and-forget, since
    cleanup cannot await, but the request is already in flight by then.

    Deliberately has no dependency array entries, so it runs on unmount only and
    not on every value change.
  */
  const latest = useRef({ values, onSaveDraft });
  latest.current = { values, onSaveDraft };

  useEffect(() => {
    return () => {
      if (!dirty.current) return;
      clearTimeout(timer.current);
      void latest.current.onSaveDraft(latest.current.values).catch(() => {
        // Nothing useful to do — the component is already gone, and the values
        // survive in the parent's cache either way.
      });
    };
  }, []);

  // Don't let someone close the tab on unsaved edits.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty.current) e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);

    const payload: SubmissionPayload = {
      sectionKey: section.key,
      values,
      submittedByName: name,
      submittedByEmail: email,
    };

    // Validate locally first for a fast answer. The API revalidates against the
    // frozen definition and that run is the gate — this is convenience only.
    const local = validateSubmission(definition, payload);
    if (!local.valid) {
      setErrors(local.errors);
      setSubmitCount((c) => c + 1);
      setSubmitting(false);
      return;
    }

    const serverErrors = await onSubmit(payload);
    if (serverErrors && serverErrors.length > 0) {
      setErrors(serverErrors);
      setSubmitCount((c) => c + 1);
    } else {
      setErrors([]);
    }
    setSubmitting(false);
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <header style={{ marginBottom: 'var(--sp-8)' }}>
        <h1
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--fs-h2)',
            fontWeight: 'var(--fw-extrabold)',
            letterSpacing: '-0.02em',
            color: 'var(--text-strong)',
          }}
        >
          {section.title}
        </h1>
        {section.intro && (
          <p
            style={{
              marginTop: 'var(--sp-3)',
              maxWidth: '62ch',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-body-lg)',
              color: 'var(--text-body)',
            }}
          >
            {section.intro}
          </p>
        )}

        {/*
          Who can finish this, said before they start.

          The person holding the invite is usually the business owner, not a
          Global Administrator. Letting them reach the bottom of a section and
          only then discover they cannot complete it wastes their time and
          stalls the onboarding — they have to find the right person, forward
          the link and explain what is needed, all without knowing what it was.
        */}
        {section.completedBy === 'globalAdmin' && (
          <p
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--sp-3)',
              marginTop: 'var(--sp-4)',
              padding: 'var(--sp-4)',
              maxWidth: '62ch',
              border: '1px solid var(--border-subtle)',
              borderLeft: '3px solid var(--accent-primary)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-body)',
              color: 'var(--text-body)',
            }}
          >
            <Icon name="alert-circle" size={20} aria-hidden />
            <span>
              <strong>This part needs a Microsoft 365 administrator.</strong> The steps below happen
              in Microsoft&rsquo;s admin centre and need someone with Global Administrator on your
              Microsoft 365. If that is not you, forward this email to whoever it is — the link works
              for them too, and the rest of your answers are saved.
            </span>
          </p>
        )}
      </header>

      {previouslySubmittedOn && (
        /*
          Without this the form just looks pre-filled and a returning customer
          cannot tell whether their last submission arrived. Saying when we got
          it, and that these are their own answers, is the difference between
          "did that work?" and "right, what changed".
        */
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--sp-3)',
            padding: 'var(--sp-4) var(--sp-5)',
            marginBottom: 'var(--sp-6)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--y-blue-200)',
            background: 'var(--y-blue-50)',
          }}
        >
          <Icon name="check-circle" size={20} color="var(--text-brand)" />
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-body)',
              color: 'var(--text-body)',
            }}
          >
            You sent us this on{' '}
            <strong>
              {new Date(previouslySubmittedOn).toLocaleDateString('en-AU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </strong>
            . Your answers are below — change anything that is out of date and send it again.
          </p>
        </div>
      )}

      <ErrorSummary errors={errors} submitCount={submitCount} />

      {section.groups.map((group) => (
        <Card key={group.key} style={{ marginBottom: 'var(--sp-6)', padding: 'var(--sp-6)' }}>
          <h2
            style={{
              margin: 0,
              marginBottom: group.description ? 'var(--sp-2)' : 'var(--sp-5)',
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--fs-h4)',
              fontWeight: 'var(--fw-bold)',
              color: 'var(--text-strong)',
            }}
          >
            {group.title}
          </h2>

          {group.description && (
            <p
              style={{
                margin: 0,
                marginBottom: 'var(--sp-5)',
                maxWidth: '62ch',
                fontFamily: 'var(--font-body)',
                color: 'var(--text-muted)',
              }}
            >
              {group.description}
            </p>
          )}

          {group.repeatable ? (
            <RepeatableGroup
              group={group}
              values={values}
              errorsByPath={errorsByPath}
              groupError={errorsByPath.get(group.key)}
              onChange={setValue}
            />
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: 'var(--sp-5)',
              }}
            >
              {group.fields.map((field) => {
                const path = `${group.key}.${field.key}`;
                return (
                  <FieldRenderer
                    key={path}
                    field={field}
                    path={path}
                    value={values[path]}
                    error={errorsByPath.get(path)}
                    errorsByPath={errorsByPath}
                    onChange={(v) => setValue(path, v)}
                  />
                );
              })}
            </div>
          )}
        </Card>
      ))}

      <Card style={{ marginBottom: 'var(--sp-6)', padding: 'var(--sp-6)' }}>
        <h2
          style={{
            margin: 0,
            marginBottom: 'var(--sp-5)',
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--fs-h4)',
            fontWeight: 'var(--fw-bold)',
          }}
        >
          Before you send this
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 'var(--sp-5)',
          }}
        >
          <Input
            id="field-submittedByName"
            label="Your name"
            value={name}
            error={errorsByPath.get('submittedByName')}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            id="field-submittedByEmail"
            label="Your email"
            type="email"
            value={email}
            error={errorsByPath.get('submittedByEmail')}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </Card>

      {/*
        Submit sits bottom-right, with the save status on the left.

        `justifyContent: flex-end` rather than `space-between`, because the save
        indicator renders nothing when idle — with space-between the lone button
        would jump to the LEFT. The indicator's own `marginRight: auto` is what
        pushes the two apart when it is present.

        The indicator comes first in the DOM so reading order matches visual
        order. It is a status message, not interactive, so tab order is
        unaffected either way.
      */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 'var(--sp-4)',
          flexWrap: 'wrap',
          paddingBottom: 'var(--sp-10)',
        }}
      >
        <SaveIndicator state={saveState} />
        <Button type="submit" size="lg" disabled={submitting} data-y365-focusable>
          {submitting ? 'Sending…' : 'Send this to Y365'}
        </Button>
      </div>
    </form>
  );
}

function SaveIndicator({ state }: { state: SaveState }) {
  const content: Record<SaveState, { icon: string; text: string; color: string } | null> = {
    idle: null,
    saving: { icon: 'loader', text: 'Saving…', color: 'var(--text-muted)' },
    saved: { icon: 'check', text: 'Saved — you can come back to this later', color: 'var(--y-success)' },
    error: { icon: 'alert-triangle', text: 'We could not save just now. Your answers are still here.', color: 'var(--y-warning)' },
  };

  const c = content[state];

  /*
    Always rendered, empty when idle — two reasons.

    A live region has to be in the DOM *before* its content changes. One that
    appears already populated is unreliably announced, which defeats the point.

    And `marginRight: auto` is what pushes the submit button to the right; with
    the element removed entirely the layout would shift as the status came and
    went.
  */
  return (
    <span
      // polite: never interrupt someone mid-field to say a draft saved
      aria-live="polite"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        marginRight: 'auto',
        minHeight: 20,
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--fs-sm)',
        color: c?.color,
      }}
    >
      {c && (
        <>
          <Icon name={c.icon} size={16} />
          {c.text}
        </>
      )}
    </span>
  );
}

interface RepeatableProps {
  group: SectionDefinition['groups'][number];
  values: Record<string, unknown>;
  errorsByPath: Map<string, string>;
  /** Cross-row error — "add at least one", "exactly one main contact". */
  groupError?: string;
  onChange: (path: string, value: unknown) => void;
}

function RepeatableGroup({ group, values, errorsByPath, groupError, onChange }: RepeatableProps) {
  const rows = Array.isArray(values[group.key])
    ? (values[group.key] as Array<Record<string, unknown>>)
    : [{}];

  const update = (index: number, key: string, value: unknown) => {
    const next = rows.map((row, i) => (i === index ? { ...row, [key]: value } : row));
    onChange(group.key, next);
  };

  return (
    // id and tabIndex so the error summary can jump here. Cross-row errors are
    // reported against the group, and without a target the summary link would
    // go nowhere.
    <div
      id={`field-${group.key}`}
      tabIndex={-1}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', outline: 'none' }}
    >
      {groupError && (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: 'var(--sp-3) var(--sp-4)',
            borderRadius: 'var(--radius-sm)',
            border: '1.5px solid var(--y-danger)',
            background: 'var(--bg-surface)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-sm)',
            color: 'var(--y-danger)',
          }}
        >
          {groupError}
        </p>
      )}
      {rows.map((row, index) => (
        <fieldset
          key={index}
          style={{
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-5)',
            margin: 0,
          }}
        >
          <legend
            style={{
              padding: '0 var(--sp-2)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-sm)',
              fontWeight: 'var(--fw-bold)',
              color: 'var(--text-muted)',
            }}
          >
            {group.title} {index + 1}
          </legend>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 'var(--sp-5)',
            }}
          >
            {group.fields.map((field) => {
              const path = `${group.key}[${index}].${field.key}`;
              return (
                <FieldRenderer
                  key={path}
                  field={field}
                  path={path}
                  value={row[field.key]}
                  error={errorsByPath.get(path)}
                  onChange={(v) => update(index, field.key, v)}
                />
              );
            })}
          </div>

          {rows.length > 1 && (
            <div style={{ marginTop: 'var(--sp-4)' }}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-y365-focusable
                onClick={() =>
                  onChange(
                    group.key,
                    rows.filter((_, i) => i !== index),
                  )
                }
              >
                Remove
              </Button>
            </div>
          )}
        </fieldset>
      ))}

      <div>
        <Button
          type="button"
          variant="secondary"
          data-y365-focusable
          leadingIcon={<Icon name="plus" size={16} />}
          onClick={() => onChange(group.key, [...rows, {}])}
        >
          {group.addLabel ?? 'Add another'}
        </Button>
      </div>
    </div>
  );
}
